import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import SearchSummaryBar from "../components/SearchSummaryBar";
import TrainResultCard from "../components/TrainResultCard";
import { STATIC_TRAINS } from "../lib/staticTrainData";
import { toolautosubmitEnabled } from "@/lib/webmcpConfig";

const DEFAULT_FILTERS = {
  minPrice: "0",
  maxPrice: "40",
  minDeparture: "00:00",
  maxDeparture: "23:59",
  minArrival: "00:00",
  maxArrival: "23:59",
};

export default function ResultsPage() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);

  const origin = params.get("origin") || "";
  const destination = params.get("destination") || "";
  const tripType = params.get("tripType") || "one-way";
  const outboundDate = params.get("outboundDate") || "";
  const inboundDate = params.get("inboundDate") || "";
  const passengers = Number(params.get("passengers")) || 1;

  const results = useMemo(
    () =>
      STATIC_TRAINS.map((t, i) => ({
        ...t,
        id: `train-${i}`,
        origin,
        destination,
        date: outboundDate,
        passengers,
        tripType,
        totalPrice: t.pricePerPerson * passengers,
      })),
    [origin, destination, outboundDate, tripType, passengers]
  );

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const toMins = (hhmm) => {
    const parts = (hhmm || "00:00").split(":").map(Number);
    return (parts[0] || 0) * 60 + (parts[1] || 0);
  };

  const filtered = useMemo(() => {
    const minP = Number(filters.minPrice) || 0;
    const maxP = Number(filters.maxPrice) || Infinity;
    const minDep = toMins(filters.minDeparture);
    const maxDep = toMins(filters.maxDeparture);
    const minArr = toMins(filters.minArrival);
    const maxArr = toMins(filters.maxArrival);

    return results.filter((t) => {
      if (t.pricePerPerson < minP || t.pricePerPerson > maxP) return false;
      const dep = toMins(t.departureTime);
      if (dep < minDep || dep > maxDep) return false;
      const arr = toMins(t.arrivalTime);
      if (arr < minArr || arr > maxArr) return false;
      return true;
    });
  }, [results, filters]);

  const handleSelect = (train) => {
    const data = encodeURIComponent(JSON.stringify(train));
    navigate(`/confirmation?data=${data}`);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const event = e.nativeEvent;
    const fd = new FormData(e.currentTarget);
    const sel = String(fd.get("selectTrain") || "").trim();

    let selectedTrain;

    if (sel) {
      const asNum = Number(sel);

      if (!Number.isNaN(asNum) && asNum >= 1 && asNum <= filtered.length) {
        selectedTrain = filtered[asNum - 1];
      } else {
        selectedTrain =
          filtered.find((t) => t.departureTime === sel) ||
          filtered.find((t) => t.arrivalTime === sel) ||
          filtered.find((t) => t.trainNumber.toLowerCase() === sel.toLowerCase()) ||
          filtered.find((t) => t.pricePerPerson === asNum);
      }

      if (selectedTrain) {
        const data = encodeURIComponent(JSON.stringify(selectedTrain));
        const resultsUrl = `/confirmation?data=${data}`;

        if (typeof event.respondWith === "function" && event.agentInvoked) {
          event.respondWith({
            ok: true,
            resultsUrl,
            selectedTrain,
          });
        }

        handleSelect(selectedTrain);
      }
    }

    const newFilters = { ...filters };
    ["minPrice", "maxPrice", "minDeparture", "maxDeparture", "minArrival", "maxArrival"].forEach((key) => {
      const val = fd.get(key);
      if (typeof val === "string" && val.trim() !== "") newFilters[key] = val.trim();
    });
    setFilters(newFilters);

    if (typeof event.respondWith === "function" && event.agentInvoked) {
      event.respondWith({
        ok: true,
        resultsUrl: "/results",
        filters: newFilters,
        matchingTrains: filtered.length,
      });
      return;
    }
  };

  const handleBackSubmit = (e) => {
    e.preventDefault();

    const event = e.nativeEvent;

    if (typeof event.respondWith === "function" && event.agentInvoked) {
      event.respondWith({
        ok: true,
        resultsUrl: "/",
      });
    }

    window.location.assign("/");
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-30 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3 mb-2">
            <form
              onSubmit={handleBackSubmit}
              toolname="backToSearch"
              tooldescription="Return to the train search page."
              {...(toolautosubmitEnabled ? { toolautosubmit: "" } : {})}
            >
              <Button type="submit" variant="ghost" size="icon" className="shrink-0">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </form>
            <h1 className="text-lg font-semibold">Train Results</h1>
          </div>
          <SearchSummaryBar
            origin={origin}
            destination={destination}
            outboundDate={outboundDate}
            inboundDate={inboundDate}
            passengers={passengers}
            tripType={tripType}
          />
        </div>
      </div>

      <form
        onSubmit={handleFormSubmit}
        toolname="filterTrainResults"
        tooldescription="Filter the current train results by price, departure time, arrival time, or select one train to continue booking."
        {...(toolautosubmitEnabled ? { toolautosubmit: "" } : {})}
        className="max-w-7xl mx-auto px-4 py-6"
      >
        <div className="flex gap-8">
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-36 bg-card border border-border rounded-xl p-5 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Filters</h3>
                <button type="button" onClick={() => setFilters(DEFAULT_FILTERS)} className="text-xs text-muted-foreground hover:text-foreground">
                  Reset
                </button>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-3">Price (CHF)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-muted-foreground">Min</label>
                    <input
                      type="text"
                      name="minPrice"
                      value={filters.minPrice}
                      onChange={(e) => setFilters((f) => ({ ...f, minPrice: e.target.value }))}
                      placeholder="0"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                      toolparamtitle="minPrice"
                      toolparamdescription="Minimum price in CHF."
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-muted-foreground">Max</label>
                    <input
                      type="text"
                      name="maxPrice"
                      value={filters.maxPrice}
                      onChange={(e) => setFilters((f) => ({ ...f, maxPrice: e.target.value }))}
                      placeholder="200"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                      toolparamtitle="maxPrice"
                      toolparamdescription="Maximum price in CHF."
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-3">Departure Time</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-muted-foreground">From</label>
                    <input
                      type="text"
                      name="minDeparture"
                      value={filters.minDeparture}
                      onChange={(e) => setFilters((f) => ({ ...f, minDeparture: e.target.value }))}
                      placeholder="HH:MM"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                      toolparamtitle="minDeparture"
                      toolparamdescription="Earliest departure time in HH:MM."
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-muted-foreground">To</label>
                    <input
                      type="text"
                      name="maxDeparture"
                      value={filters.maxDeparture}
                      onChange={(e) => setFilters((f) => ({ ...f, maxDeparture: e.target.value }))}
                      placeholder="HH:MM"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                      toolparamtitle="maxDeparture"
                      toolparamdescription="Latest departure time in HH:MM."
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-3">Arrival Time</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-muted-foreground">From</label>
                    <input
                      type="text"
                      name="minArrival"
                      value={filters.minArrival}
                      onChange={(e) => setFilters((f) => ({ ...f, minArrival: e.target.value }))}
                      placeholder="HH:MM"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                      toolparamtitle="minArrival"
                      toolparamdescription="Earliest arrival time in HH:MM."
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-muted-foreground">To</label>
                    <input
                      type="text"
                      name="maxArrival"
                      value={filters.maxArrival}
                      onChange={(e) => setFilters((f) => ({ ...f, maxArrival: e.target.value }))}
                      placeholder="HH:MM"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                      toolparamtitle="maxArrival"
                      toolparamdescription="Latest arrival time in HH:MM."
                    />
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {filtered.length} train{filtered.length !== 1 ? "s" : ""} found
              </p>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-lg font-medium text-muted-foreground">No trains match your filters</p>
                <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((train) => (
                  <TrainResultCard key={train.id} train={train} onSelect={handleSelect} />
                ))}
              </div>
            )}
          </main>
        </div>

        <div className="lg:hidden mt-6 bg-card border border-border rounded-xl p-5 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Filters</h3>
            <button type="button" onClick={() => setFilters(DEFAULT_FILTERS)} className="text-xs text-muted-foreground">
              Reset
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Min Price</label>
              <input
                type="text"
                value={filters.minPrice}
                onChange={(e) => setFilters((f) => ({ ...f, minPrice: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Max Price</label>
              <input
                type="text"
                value={filters.maxPrice}
                onChange={(e) => setFilters((f) => ({ ...f, maxPrice: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              />
            </div>
          </div>
        </div>

        <input
          type="text"
          name="selectTrain"
          placeholder="Select a train (e.g. 1, 08:05, IC 710)"
          className="w-full mt-4 flex h-10 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          toolparamtitle="selectTrain"
          toolparamdescription="Select a train by position, departure time, or train number."
        />

        <button type="submit" className="sr-only" aria-hidden="true">
          Apply filters
        </button>
      </form>
    </div>
  );
}